import i18n from '../i18n'

/**
 * Convert a YYYY-MM-DD string (or Date) to Norwegian dd.mm.yyyy display format.
 *
 * Also accepts a full ISO timestamp ("2022-05-30T13:29:51.453+02:00") and uses
 * its date part. Without that, the split below found three '-' separated
 * pieces in such a string and produced "30T13:29:51.453+02:00.05.2022" —
 * garbage that still looked date-shaped enough to ship. SVV and Brreg both
 * return timestamps rather than dates for several fields.
 *
 * The date part is taken as written rather than parsed into a Date, because
 * the strings carry their own +01:00/+02:00 offset and reparsing them in the
 * browser's zone can shift a midnight timestamp to the previous day.
 */
export function formatNorwegianDate(dateStr: string | Date): string {
  if (dateStr instanceof Date) {
    const d = String(dateStr.getDate()).padStart(2, '0')
    const m = String(dateStr.getMonth() + 1).padStart(2, '0')
    return `${d}.${m}.${dateStr.getFullYear()}`
  }
  const parts = dateStr.slice(0, 10).split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[2]}.${parts[1]}.${parts[0]}`
}

/**
 * Format a Date as YYYY-MM-DD from its local calendar date.
 *
 * Use this, not toISOString().split('T')[0], for API date params. toISOString
 * converts to UTC first, so a local-midnight Date in Europe/Oslo serializes
 * as the previous day (bilpriser-backend#297).
 */
export function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** SVV drivstoff code → fuel label key in the common namespace (#714) */
const DRIVSTOFF_KEYS = {
  '1': 'fuel.petrol',
  '2': 'fuel.diesel',
  '3': 'fuel.gas',
  '4': 'fuel.paraffin',
  '5': 'fuel.electric',
  '6': 'fuel.hydrogen',
  '7': 'fuel.lpg',
  '8': 'fuel.cng',
  '9': 'fuel.other',
  '10': 'fuel.plugInHybrid',
  '13': 'fuel.biodiesel',
  '15': 'fuel.biogas',
  '21': 'fuel.ethanol',
  '22': 'fuel.methanol',
} as const

/**
 * Map SVV drivstoff codes (e.g. "1,5") to readable names (e.g. "Bensin / Elektrisk").
 */
export function formatDrivstoff(codes: string | null | undefined): string {
  if (!codes) return '—'
  return codes
    .split(',')
    .map(c => {
      const key = DRIVSTOFF_KEYS[c.trim() as keyof typeof DRIVSTOFF_KEYS]
      return key ? i18n.t(key) : c.trim()
    })
    .filter((v, i, a) => a.indexOf(v) === i) // dedupe (e.g. "5,5,5" → "Elektrisk")
    .join(' / ')
}

